import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const TermsOfServiceContent = () => (
  <div className="max-w-3xl mx-auto bg-white shadow-sm rounded-lg p-8 space-y-6">
    <header>
      <p className="text-sm uppercase tracking-wide text-indigo-500 font-semibold">Legal</p>
      <h1 className="text-3xl font-bold text-gray-900 mt-2">Terms of Service</h1>
      <p className="text-gray-600 mt-2">
        Last updated: {new Date().toLocaleDateString()}
      </p>
      <p className="text-gray-600">
        These Terms of Service ("Terms") govern access to and use of the PromptCRM: Smart Email Management operated by Animatic Media Inc. ("we", "our", or "Service").
      </p>
    </header>

    {/* Acceptance */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">1. Acceptance of Terms</h2>
      <p className="text-gray-600">
        By accessing or using the Service, you agree to comply with these Terms. If you do not agree, you may not use the Service.
      </p>
    </section>

    {/* Accounts */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">2. User Accounts</h2>

      <p className="text-gray-600">
        Users must provide accurate account information and are responsible for maintaining account security.
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>You are responsible for all activity under your account.</li>
        <li>You must not share login credentials with unauthorized parties.</li>
        <li>You must notify us immediately if unauthorized access occurs.</li>
      </ul>
    </section>

    {/* Service Description */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">3. Service Description</h2>

      <p className="text-gray-600">
        The PromptCRM: Smart Email Management provides tools for email campaign management, team collaboration, and analytics.
      </p>

      <p className="text-gray-600">
        The Service may integrate with third-party providers including Google Gmail APIs and payment processors.
      </p>
    </section>

    {/* Google Integration */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">4. Google Account Integration</h2>

      <p className="text-gray-600">
        Users may optionally connect Google accounts to send emails or track responses.
      </p>

      <p className="text-gray-600">
        Our use of Google data complies with Google API Services User Data Policy, including Limited Use requirements.
      </p>

      <p className="text-gray-600">
        Users may revoke Google access at any time via Google account security settings.
      </p>
    </section>

    {/* Acceptable Use */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">5. Acceptable Use</h2>

      <p className="text-gray-600">Users agree NOT to:</p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Send spam, unsolicited bulk email, or illegal communications</li>
        <li>Use purchased or harvested email lists without consent</li>
        <li>Transmit malware, phishing attempts, or fraudulent content</li>
        <li>Violate applicable laws or regulations</li>
        <li>Attempt to disrupt or interfere with system security</li>
      </ul>
    </section>

    {/* Billing */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">6. Subscriptions and Billing</h2>

      <p className="text-gray-600">
        Some Service features require paid subscriptions. Payments are processed through third-party providers such as Stripe.
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Subscription fees are billed according to selected plans.</li>
        <li>Users are responsible for maintaining valid payment methods.</li>
        <li>Failure to pay may result in service suspension.</li>
      </ul>
    </section>

    {/* Availability */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">7. Service Availability</h2>

      <p className="text-gray-600">
        We strive to maintain reliable service but do not guarantee uninterrupted availability.
      </p>

      <p className="text-gray-600">
        Maintenance, upgrades, or external provider disruptions may affect service performance.
      </p>
    </section>

    {/* Data Responsibility */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">8. User Data Responsibility</h2>

      <p className="text-gray-600">
        Users are responsible for ensuring they have permission to use contact and campaign data uploaded to the Service.
      </p>

      <p className="text-gray-600">
        We are not responsible for user-generated content or communications sent through the platform.
      </p>
    </section>

    {/* Limitation */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">9. Limitation of Liability</h2>

      <p className="text-gray-600">
        To the maximum extent permitted by law, Animatic Media Inc. is not liable for indirect, incidental, or consequential damages arising from use of the Service.
      </p>
    </section>

    {/* Termination */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">10. Account Termination</h2>

      <p className="text-gray-600">
        We reserve the right to suspend or terminate accounts that violate these Terms or applicable laws.
      </p>

      <p className="text-gray-600">
        Users may request account deletion by contacting support.
      </p>
    </section>

    {/* Changes */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">11. Changes to Terms</h2>

      <p className="text-gray-600">
        We may update these Terms periodically. Continued use of the Service indicates acceptance of updated Terms.
      </p>
    </section>

    {/* Contact */}
    <section className="space-y-2">
      <h2 className="text-xl font-semibold text-gray-800">12. Contact</h2>

      <p className="text-gray-600">
        For questions regarding these Terms, contact:
      </p>

      <p>
        <a
          href="mailto:support@animaticmedia.com"
          className="text-indigo-600 hover:text-indigo-500"
        >
          support@animaticmedia.com
        </a>
      </p>
    </section>
  </div>
);

const TermsOfServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard?tab=terms', { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <TermsOfServiceContent />
    </div>
  );
};

export default TermsOfServicePage;
